import { div, span } from './dom';
import { InputCallbackType } from './input_callback';
import { Constants } from './constants';
import { DateLocale } from './date_locale';

const month_day_counts = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const data_min_key = "data-min";
const data_max_key = "data-max";
const data_value_key = "data-value";
const partial_key = "data-value-partial";

export interface DateInputCallback {
  (date: Date | null, type: InputCallbackType): void;
}

interface DateValues {
  month: number;
  day: number;
  year: number;
  hour: number;
  minute: number;
  period: string;
}

export enum DatePickerType {
  DateTime,
  Date,
}

export interface DateInputOptions {
  UTC: boolean;
  Force24Hour: boolean,
  ShowCalendar: boolean,
  Type: DatePickerType,
  ShowClear: boolean,
}

function viewDateAMPMInput(initial_value: string, cb: (t: InputCallbackType) => any, prev_cb: any, next_cb: any, disabled: boolean, focus_cb?: Function, blur_cb?: Function): HTMLDivElement {
  const d = div("two-digit", "am-pm");

  function update(s: string) {
    d.setAttribute(data_value_key, s);
    d.innerHTML = s;
  }

  update(initial_value);
  if (!disabled) {
    d.tabIndex = 0;
  }

  function toggle() {
    if (d.getAttribute(data_value_key) == Constants.period_am) {
      update(Constants.period_pm);
    } else {
      update(Constants.period_am);
    }
  }

  d.onkeydown = function (e) {
    if (e.keyCode === 37 || e.key == Constants.key_left) { // left
      prev_cb();
      e.preventDefault();
    } else if (e.keyCode === 38 || e.key == Constants.key_up) { // up
      toggle();
      cb(InputCallbackType.Input);
      e.preventDefault();
    } else if (e.keyCode === 39 || e.key == Constants.key_right) { // right
      next_cb();
      e.preventDefault();
    } else if (e.keyCode === 40 || e.key == Constants.key_down) { // down
      toggle();
      cb(InputCallbackType.Input);
      e.preventDefault();
    } else if (e.keyCode === 65 || e.key == "a") { // a
      update(Constants.period_am);
      e.preventDefault();
      cb(InputCallbackType.Input);
    } else if (e.keyCode === 80 || e.key == "p") { // p
      update(Constants.period_pm);
      e.preventDefault();
      cb(InputCallbackType.Input);
    } else if (e.keyCode == 13 || e.key == Constants.key_enter) { // enter
      cb(InputCallbackType.Enter);
      e.preventDefault();
    } else if (e.keyCode == 27 || e.key == Constants.key_escape) { // esc
      cb(InputCallbackType.Escape);
      e.preventDefault();
      e.stopPropagation();
    }
  };
  d.onfocus = function () {
    if (focus_cb !== undefined) {
      focus_cb();
    }
  };
  d.onblur = function () {
    if (blur_cb !== undefined) {
      blur_cb();
    }
  };
  return d;
}

function updateNumberInput(elem: HTMLDivElement, n: number, pad_label: boolean, concat: boolean = false) {
  let max_string = elem.getAttribute(data_max_key) || "";
  const max = Number(max_string);
  const max_characters = max_string.length;
  let label = String(n);
  if (concat) {
    let s = elem.getAttribute(partial_key);
    if (!s) {
      s = "";
    }
    if (s.length >= max_characters) {
      s = s.slice(-(max_characters - 1));
    }
    if (Number(s + String(n)) <= max) {
      s = s + String(n);
      elem.setAttribute(partial_key, s);
      label = s;
    } else {
      elem.setAttribute(partial_key, String(n));
    }
  } else {
    elem.setAttribute(data_value_key, String(n));
  }
  if (pad_label && label.length === 1) {
    label = "0" + label;
  }
  elem.innerHTML = label;
}

export function viewNumberInput(initial_value: number, min: number, initial_max: number, cb: (t: InputCallbackType) => any, prev_cb: Function, next_cb: Function, range_cb: Function, disabled: boolean, focus_cb?: Function, blur_cb?: Function, pad_label: boolean = false): HTMLDivElement {
  const d = div();
  if (String(initial_value).length < 3) {
    d.classList.add("two-digit");
  } else {
    d.classList.add("four-digit");
  }

  const previous_key = "data-value-previous";
  const timer_key = "data-timer";

  d.setAttribute(data_max_key, String(initial_max));

  function applyPartial(t: InputCallbackType) {
    const s = d.getAttribute(partial_key);
    if (!s) {
      return;
    }
    let n = Number(s);
    const max_string = d.getAttribute(data_max_key) || "";
    const max = Number(max_string);
    const val = Number(d.getAttribute(data_value_key));
    if (n < min) {
      n = val;
    } else if (n > max) {
      n = val;
    }
    d.setAttribute(partial_key, "");
    const ok = range_cb(n);
    if (!ok) {
      n = Number(d.getAttribute(previous_key));
    }
    updateNumberInput(d, n, pad_label);
    if (ok) {
      cb(t);
    }
  }

  updateNumberInput(d, initial_value, pad_label);
  if (!disabled) {
    d.tabIndex = 0;
  }

  const up_input = function () {
    let value = Number(d.getAttribute(data_value_key));
    const max_string = d.getAttribute(data_max_key) || "";
    const max = Number(max_string);
    if (value + 1 <= max) {
      value++;
    } else {
      value = min;
    }
    const ok = range_cb(value);
    if (ok) {
      updateNumberInput(d, value, pad_label);
      cb(InputCallbackType.Input);
    }
  };

  const down_input = function () {
    let value = Number(d.getAttribute(data_value_key));
    const max_string = d.getAttribute(data_max_key) || "";
    const max = Number(max_string);
    if (value - 1 >= min) {
      value--;
    } else {
      value = max;
    }
    const ok = range_cb(value);
    if (ok) {
      updateNumberInput(d, value, pad_label);
      cb(InputCallbackType.Input);
    }
  };

  const number_input = function (n: number) {
    const v = d.getAttribute(data_value_key);
    if (typeof (v) === "string") {
      d.setAttribute(previous_key, v);
    }
    const _t = d.getAttribute(timer_key);
    if (_t) {
      clearTimeout(Number(_t));
    }
    const t = setTimeout(function () {
      applyPartial(InputCallbackType.Change);
    }, 500);
    d.setAttribute(timer_key, String(t));
    updateNumberInput(d, n, pad_label, true);
  };

  d.onkeydown = function (e) {
    const num = Number(e.key);
    if (e.keyCode === 37 || e.key == Constants.key_left) { // left
      prev_cb();
      e.preventDefault();
    } else if (e.keyCode === 38 || e.key == Constants.key_up) { // up
      up_input();
      e.preventDefault();
    } else if (e.keyCode === 39 || e.key == Constants.key_right) { // right
      next_cb();
      e.preventDefault();
    } else if (e.keyCode === 40 || e.key == Constants.key_down) { // down
      down_input();
      e.preventDefault();
    } else if (e.keyCode == 13 || e.key == Constants.key_enter) { // enter
      const s = d.getAttribute(partial_key);
      if (s) {
        applyPartial(InputCallbackType.Enter);
      } else {
        cb(InputCallbackType.Enter);
      }
      e.preventDefault();
    } else if (e.keyCode == 27 || e.key == Constants.key_escape) { // esc
      cb(InputCallbackType.Escape);
      e.preventDefault();
      e.stopPropagation();
    } else if (e.keyCode > 47 && e.keyCode < 58 || num >= 0 && num < 10) { // numeric
      let n = e.keyCode - 48;
      if (n < 0) {
        n = num;
      }
      number_input(n);
      e.preventDefault();
    }
  };
  d.onkeyup = function (e) {
    if (e.keyCode === 38 || e.key == Constants.key_up) { // up
      cb(InputCallbackType.Change);
      e.preventDefault();
    } else if (e.keyCode === 40 || e.key == Constants.key_down) { // down
      cb(InputCallbackType.Change);
      e.preventDefault();
    }
  };
  d.onfocus = function () {
    if (focus_cb !== undefined) {
      focus_cb();
    }
  };
  d.onblur = function () {
    if (blur_cb !== undefined) {
      blur_cb();
    }
  };
  d.onclick = function (e) {
    e.stopPropagation();
  }
  d.onmousedown = function (e) {
    e.stopPropagation();
  }
  return d;
}

function renderCalendar(elem: HTMLDivElement, start: Date, selected: Date, opts: DateInputOptions, cb: DateInputCallback) {
  elem.innerHTML = "";
  const month = start.getMonth();
  const year = start.getFullYear();
  const first = new Date(year, month);
  const days = new Date(year, month + 1, 0).getDate();
  let offset = first.getDay() - 1; // monday based
  if (offset < 0) {
    offset = 6;
  }

  const table = document.createElement("table");

  const prev = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  prev.classList.add("prev");
  prev.setAttribute("viewBox", "0 0 20 20");
  const p1 = document.createElementNS("http://www.w3.org/2000/svg", "path");
  p1.setAttribute("d", "M1 9L19 9");
  prev.appendChild(p1);
  const p2 = document.createElementNS("http://www.w3.org/2000/svg", "path");
  p2.setAttribute("d", "M6 4L1 9L6 14");
  prev.appendChild(p2);
  prev.onclick = function () {
    const d = new Date(start.getFullYear(), start.getMonth() - 1);
    renderCalendar(elem, d, selected, opts, cb);
  }

  const next = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  next.classList.add("next");
  next.setAttribute("viewBox", "0 0 20 20");
  const n1 = document.createElementNS("http://www.w3.org/2000/svg", "path");
  n1.setAttribute("d", "M1 9L19 9");
  next.appendChild(n1);
  const n2 = document.createElementNS("http://www.w3.org/2000/svg", "path");
  n2.setAttribute("d", "M14 4L19 9L14 14");
  next.appendChild(n2);
  next.onclick = function () {
    const d = new Date(start.getFullYear(), start.getMonth() + 1);
    renderCalendar(elem, d, selected, opts, cb);
  }

  const title_row = document.createElement("tr");
  const title = document.createElement("th");
  title.classList.add("title");
  title.colSpan = 7;
  title.innerText = start.toLocaleString(undefined, { month: "long", year: "numeric" });
  title.appendChild(prev);
  title.appendChild(next);
  title_row.appendChild(title);
  table.appendChild(title_row);

  const labels = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
  const header_row = document.createElement("tr");
  header_row.classList.add("labels")
  for (const label of labels) {
    const td = document.createElement("td");
    td.innerText = label;
    header_row.appendChild(td);
  }
  table.appendChild(header_row);

  const today = new Date();
  let idx = offset * -1;
  while (idx <= days) {
    const tr = document.createElement("tr");
    for (let i = 0; i < 7; i++) {
      const td = document.createElement("td");
      if (idx >= 0 && idx < days) {
        td.classList.add("date-option");
        const day = idx + 1;
        td.innerText = day.toString();
        if (selected.getDate() === day && selected.getMonth() === month && selected.getFullYear() === year) {
          td.classList.add("selected", "key_color_background");
        }
        const d = new Date(year, month, day);
        if (today.getDate() === d.getDate() && today.getMonth() === d.getMonth() && today.getFullYear() === d.getFullYear()) {
          td.classList.add("today");
        }
        td.addEventListener("click", function () {
          const els = table.querySelectorAll("td");
          for (let i = 0; i < els.length; i++) {
            const el = els[i];
            el.classList.remove("selected", "key_color_background");
          }
          td.classList.add("selected", "key_color_background");
          const d = new Date(year, month, day);
          selected = d;
          cb(d, InputCallbackType.Change);
        });
      }
      tr.appendChild(td);
      idx++;
    }
    table.appendChild(tr);
  }

  const tr = document.createElement("tr");
  const td = document.createElement("td");
  td.setAttribute("colspan", "7");
  td.classList.add("button-contain");
  tr.appendChild(td);
  table.appendChild(tr);
  const buttons = div("buttons")
  td.appendChild(buttons);

  if (opts.ShowClear) {
    const c = span("button");
    c.innerText = "Clear";
    buttons.appendChild(c);
    c.onclick = function () {
      cb(null, InputCallbackType.Clear);
    }
    const space = span("button-space");
    buttons.appendChild(space);
  }

  const d = span("button");
  d.innerText = "Done";
  buttons.appendChild(d);
  d.onclick = function () {
    cb(selected, InputCallbackType.Done);
  }

  elem.appendChild(table);
}

function viewCalendarInput(date: Date, opts: DateInputOptions, cb: DateInputCallback): HTMLDivElement {
  const cal = div("calendar");
  renderCalendar(cal, date, date, opts, cb);
  return cal;
}

export function viewDatePicker(date: Date, min: Date, max: Date, disabled: boolean, cb: DateInputCallback, options: DateInputOptions, focus_cb?: Function, blur_cb?: Function): HTMLDivElement {
  const wrapper = div("date-selector-wrapper");

  const d = div("date-selector");
  if (disabled) {
    d.classList.add("disabled");
  }
  d.setAttribute(data_min_key, String(Number(min)));
  d.setAttribute(data_max_key, String(Number(max)));

  let twelve_hour = false;
  if (!options.Force24Hour) {
    twelve_hour = DateLocale.formatTwelveHour();
  }
  const day_first = DateLocale.formatDayFirst();
  const month = options.UTC ? date.getUTCMonth() : date.getMonth();
  const day = options.UTC ? date.getUTCDate() : date.getDate();;
  const year = options.UTC ? date.getUTCFullYear() : date.getFullYear();
  let hour = options.UTC ? date.getUTCHours() : date.getHours();
  const minute = options.UTC ? date.getUTCMinutes() : date.getMinutes();
  var am_pm = hour >= 12 ? Constants.period_pm : Constants.period_am;
  if (twelve_hour) {
    hour = hour % 12;
    if (hour == 0) {
      hour = 12;
    }
  }

  const date_lock = "date-lock";
  const date_active = "date-active";

  const on_fucus = function () {
    if (focus_cb !== undefined) {
      focus_cb();
    }
    wrapper.classList.add("focused");
    wrapper.classList.add(date_active);
    wrapper.setAttribute(date_lock, "true");
    window.setTimeout(clear_date_lock, 100);
  }

  const on_blur = function () {
    if (blur_cb !== undefined) {
      blur_cb();
    }
    wrapper.classList.remove("focused");
    window.setTimeout(clear_date_active, 50);
  }

  const clear_date_active = function () {
    if (wrapper.getAttribute(date_lock) === "true") {
      return;
    }
    wrapper.classList.remove(date_active);
  }

  const clear_date_lock = function () {
    wrapper.removeAttribute(date_lock);
  }

  const date_val = function (values: DateValues): number {
    if (twelve_hour && values.period == Constants.period_pm && values.hour < 12) {
      values.hour += 12;
    }
    if (options.UTC) {
      return Date.UTC(values.year, values.month, values.day, values.hour, values.minute);
    }
    const d = new Date(values.year, values.month, values.day, values.hour, values.minute);
    return Number(d);
  }

  let first_d: HTMLDivElement;
  const month_d = viewNumberInput((month + 1), 1, 12, update, function (_: any) {
    last_d.focus();
  }, function (_: any) {
    day_d.focus();
  }, function (data: number) {
    const values = data_values();
    values.month = data - 1;
    const new_d = date_val(values);
    return new_d >= Number(d.getAttribute(data_min_key)) && new_d < Number(d.getAttribute(data_max_key));
  }, disabled, on_fucus, on_blur);

  const day_d = viewNumberInput(day, 1, month_day_counts[month], update, function (_: any) {
    month_d.focus();
  }, function (_: any) {
    year_d.focus();
  }, function (data: number) {
    const values = data_values();
    values.day = data;
    const new_d = date_val(values);
    return new_d >= Number(d.getAttribute(data_min_key)) && new_d < Number(d.getAttribute(data_max_key));
  }, disabled, on_fucus, on_blur);

  if (day_first) {
    first_d = day_d;
  } else {
    first_d = month_d;
  }

  const year_d = viewNumberInput(year, 1970, 2037, update, function (_: any) {
    day_d.focus();
  }, function (_: any) {
    hour_d.focus();
  }, function (data: number) {
    const values = data_values();
    values.year = data;
    const new_d = date_val(values);
    return new_d >= Number(d.getAttribute(data_min_key)) && new_d < Number(d.getAttribute(data_max_key));

  }, disabled, on_fucus, on_blur);

  const min_hour = twelve_hour ? 1 : 0;
  const max_hour = twelve_hour ? 12 : 23;
  const hour_d = viewNumberInput(hour, min_hour, max_hour, update, function (_: any) {
    year_d.focus();
  }, function (_: any) {
    minute_d.focus();
  }, function (data: number) {
    const values = data_values();
    values.hour = data;
    const new_d = date_val(values);
    return new_d >= Number(d.getAttribute(data_min_key)) && new_d < Number(d.getAttribute(data_max_key));
  }, disabled, on_fucus, on_blur);

  const minute_d = viewNumberInput(minute, 0, 59, update, function (_: any) {
    hour_d.focus();
  }, function (_: any) {
    if (twelve_hour) {
      am_pm_d.focus();
    } else {
      first_d.focus();
    }
  }, function (data: number) {
    const values = data_values();
    values.minute = data;
    const new_d = date_val(values);
    return new_d >= Number(d.getAttribute(data_min_key)) && new_d < Number(d.getAttribute(data_max_key));
  }, disabled, on_fucus, on_blur, true);

  const am_pm_d = viewDateAMPMInput(am_pm, update, function (_: any) {
    minute_d.focus();
  }, function (_: any) {
    first_d.focus();
  }, disabled, on_fucus, on_blur);

  const last_d = twelve_hour ? am_pm_d : minute_d;

  function data_values(): DateValues {
    let period = Constants.period_am;
    let data_period = am_pm_d.getAttribute(data_value_key);
    if (data_period) {
      period = data_period;
    }
    const vals = {
      month: Number(month_d.getAttribute(data_value_key)) - 1,
      day: Number(day_d.getAttribute(data_value_key)),
      year: Number(year_d.getAttribute(data_value_key)),
      hour: Number(hour_d.getAttribute(data_value_key)),
      minute: Number(minute_d.getAttribute(data_value_key)),
      period: period,
    };
    return vals;
  }

  let cal = div();

  const cal_cb = function (d: Date | null, t: InputCallbackType) {
    let new_date: Date | null = null;
    if (d) {
      const values = data_values();
      updateNumberInput(year_d, d.getFullYear(), false);
      updateNumberInput(month_d, d.getMonth() + 1, false);
      updateNumberInput(day_d, d.getDate(), false);
      new_date = new Date(d.getFullYear(), d.getMonth(), d.getDate(), values.hour, values.minute);
    }
    cb(new_date, t);
  }

  function update(t: InputCallbackType) {
    const values = data_values();
    if (values.day > month_day_counts[values.month]) {
      values.day = month_day_counts[values.month];
      day_d.setAttribute(data_value_key, String(values.day));
      day_d.innerHTML = String(values.day);
    }
    const max = month_day_counts[values.month];
    day_d.setAttribute(data_max_key, String(max));
    const new_date = new Date(date_val(values));
    if (options.ShowCalendar) {
      renderCalendar(cal, new_date, new_date, options, cal_cb);
    }
    cb(new_date, t);
  }

  month_d.classList.add("month");
  day_d.classList.add("day");
  year_d.classList.add("year");
  hour_d.classList.add("hours");
  minute_d.classList.add("minutes");

  const s1 = span();
  s1.innerHTML = "/";
  const s2 = span();
  s2.innerHTML = "/";
  const s = span();
  const s3 = span();
  s3.innerHTML = ":";
  if (day_first) {
    d.appendChild(day_d);
  } else {
    d.appendChild(month_d);
  }
  d.appendChild(s1);
  if (day_first) {
    d.appendChild(month_d);
  } else {
    d.appendChild(day_d);
  }
  d.appendChild(s2);
  d.appendChild(year_d);
  if (options.Type === DatePickerType.DateTime) {
    d.appendChild(s);
    d.appendChild(hour_d);
    d.appendChild(s3);
    d.appendChild(minute_d);
    if (twelve_hour) {
      d.appendChild(s);
      d.appendChild(am_pm_d);
    }
  }

  const pad = function (i: number): string {
    return (i < 10 ? '0' : '') + i;
  }

  const YYYY = date.getFullYear();
  const MM = pad(date.getMonth() + 1);
  const DD = pad(date.getDate());
  const HH = pad(date.getHours());
  const II = pad(date.getMinutes());
  let date_str = "";

  const datetime = document.createElement("input");
  if (options.Type === DatePickerType.DateTime) {
    datetime.setAttribute("type", "datetime-local");
    date_str = YYYY + '-' + MM + '-' + DD + 'T' + HH + ':' + II;
  } else {
    datetime.setAttribute("type", "date");
    date_str = YYYY + '-' + MM + '-' + DD;
  }
  datetime.value = date_str;
  datetime.min = min.toUTCString();
  datetime.max = max.toUTCString();
  datetime.setAttribute("default", date_str);

  datetime.onchange = function () {
    if (datetime.value.length == 0) {
      const default_date = datetime.getAttribute("default");
      if (default_date) {
        datetime.value = default_date;
      }
    }

    // datetime-local returns dates in the format: "2019-06-17T18:07"
    const parts = datetime.value.split("T");
    const date_parts = parts[0].split("-");
    const new_date = new Date();
    const year = Number(date_parts[0]);
    const month = Number(date_parts[1]) - 1;
    const day = Number(date_parts[2]);
    new_date.setFullYear(year);
    new_date.setMonth(month);
    new_date.setDate(day);
    new_date.setHours(0);
    new_date.setMinutes(0);
    new_date.setSeconds(0);
    new_date.setMilliseconds(0);
    if (options.Type === DatePickerType.DateTime) {
      const time_parts = parts[1].split(":");
      const hour = Number(time_parts[0]);
      const minute = Number(time_parts[1]);
      new_date.setHours(hour);
      new_date.setMinutes(minute);
    }
    cb(new_date, InputCallbackType.Change);
  }

  wrapper.appendChild(d);
  wrapper.appendChild(datetime);

  if (options.ShowCalendar) {
    cal = viewCalendarInput(date, options, cal_cb);
    wrapper.appendChild(cal);
    cal.tabIndex = -1;

    cal.addEventListener("focus", function () {
      wrapper.classList.add("focused");
      wrapper.setAttribute(date_lock, "true");
      window.setTimeout(clear_date_lock, 100);
    });

    cal.addEventListener("blur", function () {
      wrapper.classList.remove("focused");
      window.setTimeout(clear_date_active, 50);
    });
  }

  return wrapper;
}
